/**
 * ヘッダーナビの制御クラス
 * - SERVICEボタン：プルダウンの開閉
 * - ハンバーガー(→✕)：ドロワーの開閉（閉じるも同ボタンに集約）
 * - オーバーレイ／Escapeキー：クローズ
 * ARIA属性とbodyスクロールロックも管理する。jQuery不使用。
 * @example
 * new HeaderNav();
 */
import { getLenis } from "./lib-smooth-scroll"

/** SERVICEプルダウン：ホバーが外れてから閉じるまでの猶予(ms)。ボタンとプルダウンの隙間(8px)を横切ってもちらつかせない */
const PULLDOWN_CLOSE_DELAY = 300

export class HeaderNav {
  serviceBtn: HTMLElement | null
  serviceItem: HTMLElement | null
  pulldown: HTMLElement | null
  pulldownHoverTimer: number | undefined
  hamburger: HTMLElement | null
  drawer: HTMLElement | null
  overlay: HTMLElement | null
  main: HTMLElement | null
  footer: HTMLElement | null
  logo: HTMLElement | null
  logoIO: IntersectionObserver | null = null
  resizeTimer: number | undefined

  constructor() {
    this.serviceBtn = document.querySelector(".header__navService")
    this.serviceItem = document.querySelector(".header__navListItem--service")
    this.pulldown = document.querySelector(".header__pulldown")
    this.hamburger = document.querySelector(".header__navHamburger")
    this.drawer = document.querySelector(".header__drawer")
    this.overlay = document.querySelector("#js-header-overlay")
    this.main = document.querySelector("main")
    this.footer = document.querySelector(".footer")
    this.logo = document.querySelector(".header__logo")

    // ヘッダーが無いページでは初期化しない
    if (!this.serviceBtn && !this.hamburger) return

    this.bindEvents()
    this.setupLogoObserver()
  }

  /**
   * イベントリスナーを登録
   * @private
   */
  bindEvents = () => {
    this.serviceBtn?.addEventListener("click", this.onServiceClick)
    // PCのみ：SERVICE項目(ボタン＋プルダウンを内包する<li>)のホバー/フォーカスでプルダウンを開閉。
    // プルダウンは<li>の子なので、ボタン→プルダウンへマウス移動しても「内側」扱いで閉じない。
    // 開くたびに幅判定するのでリサイズ自動対応＝SP(≤1024)は無効（プルダウン自体もCSSで非表示）。
    this.serviceItem?.addEventListener("mouseenter", this.openPulldownByHover)
    this.serviceItem?.addEventListener("mouseleave", this.closePulldownByHover)
    this.serviceItem?.addEventListener("focusin", this.openPulldownByHover)
    this.serviceItem?.addEventListener("focusout", this.onServiceFocusOut)
    this.hamburger?.addEventListener("click", this.toggleDrawer)
    this.overlay?.addEventListener("click", this.closeAll)
    document.addEventListener("keydown", this.onKeydown)
    window.addEventListener("resize", this.onResize)
  }

  /**
   * ロゴが暗いフッターに重なったら白くする監視をセットアップ（PC・固定ヘッダー時のみ）。
   * 固定ロゴの中心Yを「トリガーライン」にした rootMargin で footer を IntersectionObserver 監視し、
   * フッターがその線に達している間だけ .header__logo に is-onDark を付ける。
   * SP（≤1024px）はロゴが position:absolute でフッターに重ならないため対象外。
   * @private
   */
  setupLogoObserver = () => {
    this.logoIO?.disconnect()
    if (!this.logo || !this.footer) return

    // SP はロゴがページ先頭で流れる（フッターに重ならない）ので監視しない
    if (window.innerWidth <= 1024) {
      this.logo.classList.remove("is-onDark")
      return
    }

    // fixed 要素なので viewport 上のY位置は一定。中心Yを1pxのトリガーラインにする
    const rect = this.logo.getBoundingClientRect()
    const lineY = rect.top + rect.height / 2
    const vh = window.innerHeight

    this.logoIO = new IntersectionObserver(
      ([entry]) => this.logo?.classList.toggle("is-onDark", entry.isIntersecting),
      { rootMargin: `-${lineY}px 0px -${vh - lineY - 1}px 0px` }
    )
    this.logoIO.observe(this.footer)
  }

  /**
   * リサイズ時：ロゴ位置(vw基準)とビューポート高が変わるので監視を作り直す（デバウンス200ms）
   * @private
   */
  onResize = () => {
    window.clearTimeout(this.resizeTimer)
    this.resizeTimer = window.setTimeout(this.setupLogoObserver, 200)
  }

  /**
   * SERVICEボタンのクリック。プルダウンはホバー/フォーカスで開閉するので、クリックは常にサービス概要へ遷移。
   * PC/SP とも swup(非同期遷移)で遷移させる。SPは下部固定バーのSERVICEもこれ経由（旧: window.location で
   * フルリロードしていたのを swup に統一）。<button> は swup が拾わないので navigateViaSwup で委任させる。
   * @private
   */
  onServiceClick = () => {
    const url = this.serviceBtn?.dataset.spHref
    if (!url) return
    this.navigateViaSwup(url)
  }

  /**
   * <button> クリックからでも swup 遷移させる。swup は a[href] のクリックを委任で拾うため、
   * 一時的な非表示 <a> を生成してプログラム的にクリックする（除外ページなら通常遷移にフォールバック）。
   * @private
   * @param {string} url - 遷移先URL
   */
  navigateViaSwup = (url: string) => {
    const a = document.createElement("a")
    a.href = url
    a.style.display = "none"
    document.body.appendChild(a)
    a.click()
    a.remove()
  }

  /**
   * PCのみ：SERVICE項目にホバー/フォーカスが入ったらプルダウンを開く（閉じ猶予タイマーは解除）。
   * @private
   */
  openPulldownByHover = () => {
    if (window.innerWidth <= 1024) return
    window.clearTimeout(this.pulldownHoverTimer)
    this.openPulldown()
  }

  /**
   * PCのみ：SERVICE項目からマウスが出たら、猶予をおいて閉じる。
   * @private
   */
  closePulldownByHover = () => {
    if (window.innerWidth <= 1024) return
    this.scheduleClosePulldown()
  }

  /**
   * PCのみ：フォーカスが SERVICE 項目の外へ出たら閉じる（項目内でのフォーカス移動では閉じない）。
   * @private
   * @param {FocusEvent} e - focusout イベント
   */
  onServiceFocusOut = (e: FocusEvent) => {
    if (window.innerWidth <= 1024) return
    const next = e.relatedTarget as Node | null
    if (next && this.serviceItem?.contains(next)) return
    this.scheduleClosePulldown()
  }

  /**
   * 猶予(≈300ms)後にプルダウンを閉じる。ボタンとプルダウンの隙間(8px)を横切っても閉じないための猶予。
   * @private
   */
  scheduleClosePulldown = () => {
    window.clearTimeout(this.pulldownHoverTimer)
    this.pulldownHoverTimer = window.setTimeout(this.closePulldown, PULLDOWN_CLOSE_DELAY)
  }

  /**
   * プルダウンを開く（ドロワーは閉じる）。ホバー起点なのでオーバーレイ(全画面暗転)は出さない。
   * @private
   */
  openPulldown = () => {
    this.closeDrawer()
    this.pulldown?.classList.add("is-show")
    this.serviceBtn?.classList.add("is-open")
    this.serviceBtn?.setAttribute("aria-expanded", "true")
    this.setHidden(this.pulldown, false)
  }

  /**
   * プルダウンを閉じる
   * @private
   */
  closePulldown = () => {
    window.clearTimeout(this.pulldownHoverTimer)
    this.pulldown?.classList.remove("is-show")
    this.serviceBtn?.classList.remove("is-open")
    this.serviceBtn?.setAttribute("aria-expanded", "false")
    this.setHidden(this.pulldown, true)
  }

  /**
   * ドロワーの開閉をトグル（ハンバーガー→✕のトグル動作）
   * @private
   */
  toggleDrawer = () => {
    if (this.drawer?.classList.contains("is-open")) {
      this.closeAll()
    } else {
      this.openDrawer()
    }
  }

  /**
   * 要素の transform-origin を「現在の画面中央」に合わせる（scale の基準点を画面中央に）。
   * innerHeight/2 - rect.top ＝ その要素内での画面中央位置。縦長mainも下部のfooterも同じ絶対中心で縮む。
   * @private
   * @param {HTMLElement | null} el 対象要素
   */
  setCenterOrigin = (el: HTMLElement | null) => {
    if (!el) return
    el.style.transformOrigin = `center ${window.innerHeight / 2 - el.getBoundingClientRect().top}px`
  }

  /**
   * ドロワーを開く（プルダウンは閉じる・bodyスクロールロック）
   * @private
   */
  openDrawer = () => {
    this.closePulldown()
    // scale(0.98)の基準を「開いた瞬間の画面中央」に（main・footerを同じ絶対中心点で縮める）
    this.setCenterOrigin(this.main)
    this.setCenterOrigin(this.footer)
    this.drawer?.classList.add("is-open")
    this.hamburger?.classList.add("is-open")
    this.hamburger?.setAttribute("aria-expanded", "true")
    this.setHidden(this.drawer, false)
    this.showOverlay()
    document.body.classList.add("is-menuLock")
    getLenis()?.stop() // PC:Lenis慣性スクロールを停止（overflowでは止まらないため）。SPはLenis無効なので影響なし
  }

  /**
   * ドロワーを閉じる
   * @private
   */
  closeDrawer = () => {
    this.drawer?.classList.remove("is-open")
    this.hamburger?.classList.remove("is-open")
    this.hamburger?.setAttribute("aria-expanded", "false")
    this.setHidden(this.drawer, true)
    document.body.classList.remove("is-menuLock")
    getLenis()?.start() // Lenis慣性スクロールを再開
  }

  /**
   * プルダウン・ドロワー・オーバーレイを全て閉じる
   * @private
   */
  closeAll = () => {
    this.closePulldown()
    this.closeDrawer()
    this.hideOverlay()
  }

  /**
   * オーバーレイを表示
   * @private
   */
  showOverlay = () => {
    this.overlay?.classList.add("is-show")
  }

  /**
   * オーバーレイを非表示
   * @private
   */
  hideOverlay = () => {
    this.overlay?.classList.remove("is-show")
  }

  /**
   * aria-hidden / inert を切り替え
   * @private
   * @param {HTMLElement | null} el - 対象要素
   * @param {boolean} hidden - 非表示にするか
   */
  setHidden = (el: HTMLElement | null, hidden: boolean) => {
    if (!el) return
    el.setAttribute("aria-hidden", hidden.toString())
    if (hidden) {
      el.setAttribute("inert", "")
    } else {
      el.removeAttribute("inert")
    }
  }

  /**
   * キーボード操作（Escapeで全て閉じる）
   * @private
   * @param {KeyboardEvent} e - キーボードイベント
   */
  onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.closeAll()
    }
  }

  /**
   * swup 遷移後に main 参照を貼り直す。
   * header 自体は <main> 外で永続だが、this.main は遷移で差し替わる（detachedになる）ため、
   * ドロワー開閉の transform-origin 基準が狂わないよう page:view で更新する。
   */
  refreshMain = () => {
    this.main = document.querySelector("main")
  }
}

/** ヘッダーナビの単一インスタンス。swup-init が page:view で refreshMain / visit:start で closeAll を呼ぶ */
export const headerNav = new HeaderNav()
